import { HolderOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Form, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";

import {
  HOLLYWOOD_CART_MARKER,
  hollywoodHiddenSections,
  hollywoodSectionList,
  hollywoodSectionsFromList,
  type Hollywood2026SectionId,
  type HollywoodSectionListItem,
  type PageConfiguration,
} from "@fitness-app/data-models/entities/CheckoutVariant";

const SECTION_LABELS: Record<Hollywood2026SectionId, string> = {
  rating: "Opinie i oceny",
  team: "Zespół (Za programem stoją ludzie)",
  appShowcase: "Aplikacja (Cały program w Twoim telefonie)",
  pricing: "Cennik / pakiety",
  guarantee: "Baner gwarancji",
  transformations: "Metamorfozy",
  faq: "FAQ",
};

type SortableState = ReturnType<typeof useSortable>;

const sortableStyle = ({ transform, transition }: Pick<SortableState, "transform" | "transition">) => ({
  transform: CSS.Transform.toString(transform),
  transition,
});

const DragHandle = ({
  label,
  activatorRef,
  attributes,
  listeners,
}: {
  label: string;
  activatorRef: SortableState["setActivatorNodeRef"];
  attributes: SortableState["attributes"];
  listeners: SortableState["listeners"];
}) => (
  <Button
    ref={activatorRef}
    type="text"
    size="small"
    aria-label={label}
    icon={<HolderOutlined />}
    className="cursor-grab touch-none"
    {...attributes}
    {...listeners}
  />
);

/** Linia „Koszyk”: przeciąga się jak sekcja, a to, co nad nią, staje nad koszykiem. */
const CartDividerRow = () => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: HOLLYWOOD_CART_MARKER,
  });

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle({ transform, transition })}
      className={`flex items-center gap-2 px-2 py-1 ${isDragging ? "relative z-10 rounded-md bg-white shadow-lg" : ""}`}
    >
      <DragHandle
        label="Przeciągnij koszyk"
        activatorRef={setActivatorNodeRef}
        attributes={attributes}
        listeners={listeners}
      />
      <span className="flex-1 border-0 border-t border-dashed border-gray-400" />
      <span className="text-xs font-semibold uppercase text-gray-500">Koszyk</span>
      <span className="flex-1 border-0 border-t border-dashed border-gray-400" />
    </div>
  );
};

const SectionRow = ({
  section,
  visible,
  onVisible,
}: {
  section: Hollywood2026SectionId;
  visible: boolean;
  onVisible: (visible: boolean) => void;
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: section,
  });
  const label = SECTION_LABELS[section];

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle({ transform, transition })}
      className={`flex items-center gap-2 rounded-md border border-solid border-gray-200 bg-white px-2 py-1 ${isDragging ? "relative z-10 shadow-lg" : ""}`}
    >
      <DragHandle
        label={`Przeciągnij sekcję ${label}`}
        activatorRef={setActivatorNodeRef}
        attributes={attributes}
        listeners={listeners}
      />
      <span className={`flex-1 ${visible ? "" : "text-gray-400 line-through"}`}>{label}</span>
      <Switch size="small" checked={visible} aria-label={`Pokaż sekcję ${label}`} onChange={onVisible} />
    </div>
  );
};

/**
 * Jedna lista do przeciągania: sekcje i linia „Koszyk” (`sectionOrder` + `sectionPlacement`),
 * przełącznik przy sekcji chowa ją, a ona zachowuje miejsce (`hiddenSections`).
 */
export const ProductPageSectionListField = ({ formController }: { formController: FormInstance }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const config: Pick<PageConfiguration, "sectionOrder" | "sectionPlacement" | "hiddenSections"> = {
    sectionOrder: Form.useWatch("sectionOrder", formController),
    sectionPlacement: Form.useWatch("sectionPlacement", formController),
    hiddenSections: Form.useWatch("hiddenSections", formController),
  };
  const list = hollywoodSectionList(config);
  const hidden = hollywoodHiddenSections(config);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = list.indexOf(active.id as HollywoodSectionListItem);
    const to = list.indexOf(over.id as HollywoodSectionListItem);
    formController.setFieldsValue(hollywoodSectionsFromList(arrayMove(list, from, to), hidden));
  };

  const setVisible = (section: Hollywood2026SectionId, visible: boolean) =>
    formController.setFieldsValue(
      hollywoodSectionsFromList(list, visible ? hidden.filter((item) => item !== section) : [...hidden, section]),
    );

  return (
    <Form.Item
      label="Miejsce sekcji"
      tooltip="Przeciągnij za uchwyt, żeby zmienić kolejność. Sekcje nad linią „Koszyk” stoją nad koszykiem, pod nią - pod koszykiem. Przełącznik chowa sekcję, a ona zostaje na swoim miejscu. Hero, licznik i wideo promocyjne zostają na górze, o wideo decyduje osobny przełącznik."
    >
      <Form.Item name="sectionOrder" hidden noStyle />
      <Form.Item name="sectionPlacement" hidden noStyle />
      <Form.Item name="hiddenSections" hidden noStyle />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={list} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {list.map((item) =>
              item === HOLLYWOOD_CART_MARKER ? (
                <CartDividerRow key={item} />
              ) : (
                <SectionRow
                  key={item}
                  section={item}
                  visible={!hidden.includes(item)}
                  onVisible={(visible) => setVisible(item, visible)}
                />
              ),
            )}
          </div>
        </SortableContext>
      </DndContext>
    </Form.Item>
  );
};
